import { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { TutorialVideo } from '@/types';
import { parseYoutubeVideoId } from '@/lib/helpCenter';
import {
  TutorialInput,
  createTutorial,
  deleteTutorial,
  fetchAllTutorialsAdmin,
  updateTutorial,
} from '@/services/tutorialService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

interface FormState {
  id: string | null; // null = neu
  title: string;
  description: string;
  youtubeInput: string; // URL oder ID — wird beim Speichern geparst
  category: string;
  sortOrder: string;
  showOnSignup: boolean;
  published: boolean;
}

const EMPTY_FORM: FormState = {
  id: null, title: '', description: '', youtubeInput: '',
  category: '', sortOrder: '0', showOnSignup: false, published: false,
};

export const AdminTutorialsView: React.FC = () => {
  const [videos, setVideos] = useState<TutorialVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState<FormState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setVideos(await fetchAllTutorialsAdmin());
    } catch {
      // Fehler ist im Service geloggt; Liste bleibt leer
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openEdit = (v: TutorialVideo) => setForm({
    id: v.id, title: v.title, description: v.description ?? '',
    youtubeInput: v.youtubeVideoId, category: v.category ?? '',
    sortOrder: String(v.sortOrder), showOnSignup: v.showOnSignup, published: v.published,
  });

  const handleSave = async () => {
    if (!form) return;
    const videoId = parseYoutubeVideoId(form.youtubeInput);
    if (!form.title.trim()) { setFormError('Titel fehlt.'); return; }
    if (!videoId) { setFormError('Keine gültige YouTube-URL oder Video-ID.'); return; }
    setIsSaving(true);
    setFormError(null);
    const input: TutorialInput = {
      title: form.title,
      description: form.description || null,
      youtubeVideoId: videoId,
      category: form.category || null,
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      showOnSignup: form.showOnSignup,
      published: form.published,
    };
    try {
      if (form.id) await updateTutorial(form.id, input);
      else await createTutorial(input);
      setForm(null);
      await load();
    } catch {
      setFormError('Speichern fehlgeschlagen.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (v: TutorialVideo) => {
    if (!window.confirm(`Tutorial „${v.title}" wirklich löschen?`)) return;
    try {
      await deleteTutorial(v.id);
      await load();
    } catch {
      window.alert('Löschen fehlgeschlagen.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Tutorial-Videos</h3>
        <Button size="sm" onClick={() => setForm({ ...EMPTY_FORM })}>
          <Plus className="h-4 w-4 mr-1" /> Neues Tutorial
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : videos.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Noch keine Tutorials angelegt.</p>
      ) : (
        <div className="space-y-2">
          {videos.map((v) => (
            <div key={v.id} className="flex items-center gap-4 rounded-lg border border-border bg-card p-3">
              <img
                src={`https://i.ytimg.com/vi/${v.youtubeVideoId}/default.jpg`}
                alt=""
                className="h-12 w-20 rounded object-cover bg-muted"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{v.title}</p>
                <p className="text-xs text-muted-foreground">
                  {v.category || 'Allgemein'} · Reihenfolge {v.sortOrder}
                  {v.showOnSignup && ' · Signup-Popup'}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${v.published ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
                {v.published ? 'Veröffentlicht' : 'Entwurf'}
              </span>
              <Button variant="ghost" size="sm" onClick={() => openEdit(v)} aria-label="Bearbeiten">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void handleDelete(v)} aria-label="Löschen">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={form !== null} onOpenChange={(o) => { if (!o && !isSaving) setForm(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{form?.id ? 'Tutorial bearbeiten' : 'Neues Tutorial'}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="tut-title">Titel</Label>
                <Input id="tut-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tut-yt">YouTube-URL oder Video-ID</Label>
                <Input id="tut-yt" value={form.youtubeInput} placeholder="https://youtu.be/…" onChange={(e) => setForm({ ...form, youtubeInput: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tut-desc">Beschreibung (optional)</Label>
                <Input id="tut-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tut-cat">Kategorie</Label>
                  <Input id="tut-cat" value={form.category} placeholder="z.B. Staging" onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tut-sort">Reihenfolge</Label>
                  <Input id="tut-sort" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="tut-signup" className="text-sm">Neuen Usern nach Signup zeigen</Label>
                <Switch id="tut-signup" checked={form.showOnSignup} onCheckedChange={(c) => setForm({ ...form, showOnSignup: c })} />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="tut-pub" className="text-sm">Veröffentlicht</Label>
                <Switch id="tut-pub" checked={form.published} onCheckedChange={(c) => setForm({ ...form, published: c })} />
              </div>
              {formError && <p className="text-xs text-destructive">{formError}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} disabled={isSaving}>Abbrechen</Button>
            <Button onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
